<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

import type { OffsetWithFeatureListProps } from "./OffsetWithFeatureListProps";

const props = defineProps<{
    block: PageBlock<OffsetWithFeatureListProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :background-color="styleValues.background">
        <FullSpacing class="max-w-7xl mx-auto">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-10">
                <!-- Left Column - Content -->
                <div class="lg:col-span-5">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <p :style="{ color: styleValues.brand }" class="font-semibold mb-4">
                            {{ dataValues.tag.data.text }}
                        </p>
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :text-color="styleValues.text"
                            class="mb-6 text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="text-lg opacity-70"
                        />
                    </EditableElement>
                </div>

                <!-- Right Column - Features List -->
                <div class="lg:col-span-7">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-16">
                        <EditableElement
                            v-for="feature in dataValues.features.data.items"
                            :key="feature.id"
                            :blockId="block.id"
                            :field-name="`features_${feature.id}`"
                            :hideable="false"
                        >
                            <div class="flex gap-4">
                                <svg
                                    class="w-5 h-5 flex-shrink-0 mt-1"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M16.7071 4.29289C17.0976 4.68342 17.0976 5.31658 16.7071 5.70711L8.70711 13.7071C8.31658 14.0976 7.68342 14.0976 7.29289 13.7071L3.29289 9.7071C2.90237 9.31658 2.90237 8.68342 3.29289 8.29289C3.68342 7.90237 4.31658 7.90237 4.70711 8.29289L8 11.5858L15.2929 4.29289C15.6834 3.90237 16.3166 3.90237 16.7071 4.29289Z"
                                        :fill="styleValues.brand"
                                    />
                                </svg>
                                <div>
                                    <Paragraph
                                        :text="feature.title"
                                        :text-color="styleValues.text"
                                        class="font-semibold mb-2"
                                    />
                                    <Paragraph
                                        :text="feature.description"
                                        :text-color="styleValues.text"
                                        class="text-base/7 opacity-70"
                                    />
                                </div>
                            </div>
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
